import axios from 'axios';
import { useMemo } from 'react';

// Create an instance of axios with a base URL
const apiBase = import.meta.env.VITE_API_BASE_URL || '/api';
const api = axios.create({
    baseURL: apiBase,
});

// Custom hook for using the API instance
export const useApi = () => {
    return useMemo(() => api, []);
};

// Request interceptor to add authorization and security headers
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        const sessionToken = localStorage.getItem('sessionToken');
        const csrfToken = localStorage.getItem('csrfToken');

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        if (sessionToken) {
            config.headers['X-Session-Token'] = sessionToken;
        }

        // Add CSRF token for non-GET requests
        if (config.method !== 'get' && csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't tried to refresh the token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Attempt to refresh the token
                const refreshToken = localStorage.getItem('refreshToken');
                if (!refreshToken) {
                    throw new Error('No refresh token');
                }

                const response = await api.post('/auth/refresh-token', { refreshToken });

                const { token, sessionToken, csrfToken } = response.data;

                // Update stored tokens
                localStorage.setItem('token', token);
                localStorage.setItem('sessionToken', sessionToken);
                localStorage.setItem('csrfToken', csrfToken);

                // Retry the original request
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                originalRequest.headers['X-Session-Token'] = sessionToken;
                if (originalRequest.method !== 'get') {
                    originalRequest.headers['X-CSRF-Token'] = csrfToken;
                }

                return axios(originalRequest);
            } catch (error) {
                // If refresh fails, log out the user
                localStorage.clear();
                window.location.href = '/login';
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

// --- INVOICE REQUEST FUNCTIONS ---

// Get all invoice requests (for financial manager)
export const getInvoiceRequests = async () => {
    try {
        const response = await api.get('/invoices/requests');
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Approve an invoice request
export const approveInvoiceRequest = async (requestId) => {
    try {
        const response = await api.post(`/invoices/requests/${requestId}/approve`);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Reject an invoice request
export const rejectInvoiceRequest = async (requestId, reason) => {
    try {
        const response = await api.post(`/invoices/requests/${requestId}/reject`, { reason });
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// --- AUTH FUNCTIONS ---

export const loginUser = async (email, password) => {
    try {
        const response = await api.post('/auth/login', { email, password });
        return response.data;
    } catch (error) {
        console.error('Login error details:', error);
        if (error.response) {
            // Server responded with error status
            const errorData = error.response.data;
            const msg = errorData?.message || errorData?.error || `Login failed with status ${error.response.status}`;
            console.error('Server error:', msg, errorData);
            throw new Error(msg);
        }
        else if (error.request) { 
            console.error('No response from server:', error.request);
            throw new Error('Could not connect to the server. Please try again later.'); 
        }
        else { 
            console.error('Request setup error:', error.message);
            throw new Error(error.message || 'An unexpected error occurred.'); 
        }
    }
};


// --- ADMIN CHILD FUNCTIONS ---

export const getChildren = async () => {
    try {
        const response = await api.get('/admin/children');
        return response.data;
    } catch (error) {
        if (error.response) { throw error.response.data; }
        else if (error.request) { throw new Error('Could not connect to the server.'); }
        else { throw new Error('An unexpected error occurred.'); }
    }
};

export const getChildProfile = async (childId) => {
    try {
        const response = await api.get(`/admin/children/${childId}`);
        return response.data;
    } catch (error) {
        if (error.response) { throw error.response.data; }
        else if (error.request) { throw new Error('Could not connect to the server.'); }
        else { throw new Error('An unexpected error occurred.'); }
    }
};


// --- ENQUIRY FUNCTIONS ---

// UPDATED: Fetches all enquiries, now with optional filters
export const getEnquiries = async (filters = {}) => {
    try {
        // Axios will automatically convert the filters object into URL query params
        // e.g., { search: 'Rohan' } becomes ?search=Rohan
        const response = await api.get('/enquiries', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// --- ADD THIS NEW FUNCTION ---
// Updates a specific enquiry by its ID
export const updateEnquiry = async (id, enquiryData) => {
    try {
        const response = await api.put(`/enquiries/${id}`, enquiryData);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// Submits a new enquiry form
export const createEnquiry = async (enquiryData) => {
    try {
        const response = await api.post('/enquiries', enquiryData);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};
export const createProgram = async (programData) => {
    try {
        const response = await api.post('/settings/programs', programData);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};
// Gets the list of companies that are part of the program
export const getCompanies = async (includeInactive = false, mainVendorId = null) => {
    try {
        const response = await api.get('/companies', {
            params: { includeInactive, mainVendorId }
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};
// Creates new company
export const createCompany = async (companyData) => {
    try {
        const response = await api.post('/companies', companyData);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};
// Checks if a company has a tie-up
export const checkCompanyTieUp = async (companyName) => {
    try {
        const response = await api.get(`/companies/check-tieup/${encodeURIComponent(companyName)}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// --- SETTINGS FUNCTIONS ---
export const getClassrooms = async () => {
    try {
        const response = await api.get('/admin/classrooms');
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

export const createClassroom = async (classroomData) => {
    try {
        const response = await api.post('/admin/classrooms', classroomData);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// Fetches the list of programs
export const getPrograms = async () => {
    try {
        const response = await api.get('/settings/programs');
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};  
// Converts an enquiry into a student record
export const convertEnquiryToStudent = async (enquiryId, admissionData) => {
    try {
        const response = await api.post(`/admissions/convert/${enquiryId}`, admissionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// Calculate admission fee preview with real-time calculations
export const calculateAdmissionPreview = async (data) => {
    try {
        const response = await api.post('/admissions/calculate-preview', data);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// Submit admission with fee details for approval
export const submitAdmissionForApproval = async (enquiryId, admissionData) => {
    try {
        const response = await api.post(`/admissions/submit-for-approval/${enquiryId}`, admissionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// Get pending admission approvals (center director only)
export const getPendingApprovals = async () => {
    try {
        const response = await api.get('/admissions/approvals/pending');
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// Approve an admission (center director only)
export const approveAdmission = async (approvalId, notes = '') => {
    try {
        const response = await api.post(`/admissions/approvals/${approvalId}/approve`, { notes });
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// Reject an admission (center director only)
export const rejectAdmission = async (approvalId, notes = '') => {
    try {
        const response = await api.post(`/admissions/approvals/${approvalId}/reject`, { notes });
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not connect to the server.');
    }
};

// --- MAIN VENDOR FUNCTIONS ---

// Create a new main vendor
export const createMainVendor = async (data) => {
    try {
        const response = await api.post('/main-vendors', data);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get all main vendors
export const getMainVendors = async (includeInactive = false) => {
    try {
        const response = await api.get('/main-vendors', {
            params: { includeInactive }
        });
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get single main vendor by ID
export const getMainVendor = async (id) => {
    try {
        const response = await api.get(`/main-vendors/${id}`);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Update main vendor
export const updateMainVendor = async (id, data) => {
    try {
        const response = await api.put(`/main-vendors/${id}`, data);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Delete (deactivate) main vendor
export const deleteMainVendor = async (id) => {
    try {
        const response = await api.delete(`/main-vendors/${id}`);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get revenue report for main vendor
export const getMainVendorRevenue = async (id, startDate, endDate) => {
    try {
        const response = await api.get(`/main-vendors/${id}/revenue-report`, {
            params: { startDate, endDate }
        });
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// --- COMPANY FUNCTIONS (ENHANCED) ---

// Get single company by ID
export const getCompany = async (id) => {
    try {
        const response = await api.get(`/companies/${id}`);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Update company
export const updateCompany = async (id, data) => {
    try {
        const response = await api.put(`/companies/${id}`, data);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Delete (deactivate) company
export const deleteCompany = async (id) => {
    try {
        const response = await api.delete(`/companies/${id}`);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get company contribution configuration
export const getCompanyContributionConfig = async (id) => {
    try {
        const response = await api.get(`/companies/${id}/contribution-config`);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// --- RECEIPT FUNCTIONS ---

// Create a new receipt
export const createReceipt = async (data) => {
    try {
        const response = await api.post('/receipts', data);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get receipts with filters
export const getReceipts = async (filters = {}) => {
    try {
        const response = await api.get('/receipts', { params: filters });
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get single receipt by ID
export const getReceipt = async (id) => {
    try {
        const response = await api.get(`/receipts/${id}`);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Update receipt
export const updateReceipt = async (id, data) => {
    try {
        const response = await api.put(`/receipts/${id}`, data);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get pending cash payments
export const getPendingCashPayments = async () => {
    try {
        const response = await api.get('/receipts/pending');
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Generate receipt PDF
export const generateReceiptPDF = async (id) => {
    try {
        const response = await api.get(`/receipts/${id}/pdf`, {
            responseType: 'blob'
        });
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Receipt_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// --- INVOICE FUNCTIONS ---

// Generate monthly invoices for all eligible students
export const generateMonthlyInvoices = async () => {
    try {
        const response = await api.post('/invoices/generate-monthly');
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server. Please try again later.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Generate company invoices with consolidation options
export const generateCompanyInvoices = async (month, year, consolidationType) => {
    try {
        const response = await api.post('/invoices/generate-company-invoices', {
            month,
            year,
            consolidationType
        });
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server. Please try again later.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Create an invoice request (admin)
export const createInvoiceRequest = async (requestData) => {
    try {
        const response = await api.post('/invoices/requests', requestData);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Get invoices with filtering and pagination
export const getInvoices = async (params = {}) => {
    try {
        const response = await api.get('/invoices', { params });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Download invoice PDF
export const downloadInvoicePDF = async (invoiceId, invoiceNumber, twoFACode) => {
    try {
        const response = await api.get(`/invoices/generate-pdf/${invoiceId}`, {
            responseType: 'blob',
            headers: twoFACode ? { 'x-2fa-totp': twoFACode } : {}
        });
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Update invoice status
export const updateInvoiceStatus = async (invoiceId, status) => {
    try {
        const response = await api.patch(`/invoices/${invoiceId}/status`, { status });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// --- CENTERS FUNCTIONS ---

// Get centers (all for superadmin, assigned for others)
export const getCenters = async () => {
    try {
        const response = await api.get('/centers');
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Create new center (superadmin only)
export const createCenter = async (centerData) => {
    try {
        const response = await api.post('/centers', centerData);
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Update center (superadmin only)
export const updateCenter = async (centerId, centerData) => {
    try {
        const response = await api.put(`/centers/${centerId}`, centerData);
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Get centers user can access
export const getUserAccessibleCenters = async () => {
    try {
        const response = await api.get('/centers/user-accessible');
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// --- OWNERS (SUPER ADMIN) ---
export const getOwners = async () => {
    const res = await api.get('/owners');
    return res.data;
};

export const createOwner = async ({ fullName, email, password }) => {
    const res = await api.post('/owners', { fullName, email, password });
    return res.data;
};

export const getAllCenters = async () => {
    const res = await api.get('/owners/centers');
    return res.data;
};

export const getOwnerCenters = async (ownerId) => {
    const res = await api.get(`/owners/${ownerId}/centers`);
    return res.data;
};

export const updateOwnerCenters = async (ownerId, centerIds) => {
    const res = await api.put(`/owners/${ownerId}/centers`, { centerIds });
    return res.data;
};

export const getUserRoles = async (userId) => {
    const res = await api.get(`/owners/${userId}/roles`);
    return res.data;
};

export const updateUserRoles = async (userId, roles) => {
    const res = await api.put(`/owners/${userId}/roles`, { roles });
    return res.data;
};

// --- ANALYTICS FUNCTIONS ---

// Get analytics overview
export const getAnalyticsOverview = async (params = {}, options = {}) => {
    try {
        const response = await api.get('/analytics/overview', { params, ...options });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Get demographics data
export const getAnalyticsDemographics = async (params = {}, options = {}) => {
    try {
        const response = await api.get('/analytics/demographics', { params, ...options });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Get enrollment trends
export const getAnalyticsEnrollmentTrends = async (params = {}, options = {}) => {
    try {
        const response = await api.get('/analytics/enrollment-trends', { params, ...options });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Get conversion metrics
export const getAnalyticsConversionMetrics = async (params = {}, options = {}) => {
    try {
        const response = await api.get('/analytics/conversion-metrics', { params, ...options });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Get financial overview
export const getAnalyticsFinancialOverview = async (params = {}, options = {}) => {
    try {
        const response = await api.get('/analytics/financial-overview', { params, ...options });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// Get center comparison (superadmin only)
export const getAnalyticsCenterComparison = async (options = {}) => {
    try {
        const response = await api.get('/analytics/center-comparison', { ...options });
        return response.data;
    } catch (error) {
        if (error.response) { 
            throw error.response.data; 
        } else if (error.request) { 
            throw new Error('Could not connect to the server.'); 
        } else { 
            throw new Error('An unexpected error occurred.'); 
        }
    }
};

// --- DOCUMENT MANAGEMENT FUNCTIONS ---

export const uploadDocument = async (formData) => {
    try {
        const response = await api.post('/documents/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not upload document');
    }
};

export const getDocuments = async () => {
    try {
        const response = await api.get('/documents');
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not fetch documents');
    }
};

export const deleteDocument = async (documentId) => {
    try {
        const response = await api.delete(`/documents/${documentId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Could not delete document');
    }
};

// Expense analytics
export const getExpenseAnalytics = async () => {
    try {
        const response = await api.get('/expenses/analytics');
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

// Export expenses (returns a Blob for download)
export const exportExpenses = async (params = {}) => {
    try {
        const query = new URLSearchParams(params).toString();
        const response = await api.get(`/expenses/export?${query}`, { responseType: 'blob' });
        return response;
    } catch (error) {
        if (error.response) {
            throw error.response.data;
        } else if (error.request) {
            throw new Error('Could not connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

export default api;

